import React from 'react';
import LacrosseAttackField from './LacrosseAttackField';
import {
  classifyShotLocation,
  OUTCOME_STYLE,
  ShotOutcome,
  ZONE_LABELS,
  shotDistanceYards,
} from '../../utils/shotLocation';
import useShotLocationCapture from '../../hooks/useShotLocationCapture';

export interface PickedLocation {
  x: number;
  y: number;
}

interface ShotLocationPickerProps {
  outcome: ShotOutcome | null;
  value: PickedLocation | null;
  onChange: (value: PickedLocation | null) => void;
  /** Label above the field, e.g. "Tap where the shot was taken". */
  title?: string;
  /** Show a "Skip location" action — always true for live capture (never blocks a save). */
  showSkip?: boolean;
  /** Live capture: let the stat taker hide the field (remembered on this device). */
  collapsible?: boolean;
  className?: string;
}

const ShotLocationPicker: React.FC<ShotLocationPickerProps> = ({
  outcome,
  value,
  onChange,
  title = 'Tap where the shot was taken',
  showSkip = true,
  collapsible = false,
  className = '',
}) => {
  const [captureEnabled, setCaptureEnabled] = useShotLocationCapture();
  const classification = value ? classifyShotLocation(value.x, value.y) : null;
  const distance = value ? shotDistanceYards(value.x, value.y) : null;
  const style = outcome ? OUTCOME_STYLE[outcome] : null;

  if (collapsible && !captureEnabled) {
    return (
      <div className={`w-full flex items-center justify-between gap-2 px-1 ${className}`}>
        <span className="text-xs text-gray-500">Shot location field is off</span>
        <button
          type="button"
          onClick={() => setCaptureEnabled(true)}
          className="shrink-0 text-xs font-semibold text-[#255393] underline underline-offset-2"
        >
          Show field
        </button>
      </div>
    );
  }

  return (
    <div className={`w-full flex flex-col gap-2 ${className}`}>
      <div className="flex items-center justify-between gap-2 px-1">
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-[#19133c]">{title}</span>
          <span className="text-[11px] text-gray-500">Drag to fine-tune · optional</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {value && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-xs font-semibold text-[#255393] underline underline-offset-2"
            >
              Clear
            </button>
          )}
          {collapsible && (
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setCaptureEnabled(false);
              }}
              className="text-xs font-semibold text-gray-500 hover:text-gray-700 underline underline-offset-2"
            >
              Hide field
            </button>
          )}
        </div>
      </div>

      <LacrosseAttackField
        interactive
        showZoneGuides
        onTap={(x, y) => onChange({ x, y })}
        pin={value ? { x: value.x, y: value.y, outcome } : null}
        className="max-w-[520px] mx-auto"
      />

      <div className="flex items-center justify-between gap-2 px-1 min-h-[26px]">
        {classification && distance !== null ? (
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            {style &&
              (style.shape === 'diamond' ? (
                <span
                  className="inline-block w-2.5 h-2.5 rotate-45 border border-white"
                  style={{ backgroundColor: style.color }}
                />
              ) : style.shape === 'square' ? (
                <span
                  className="inline-block w-2.5 h-2.5 border border-white"
                  style={{ backgroundColor: style.color }}
                />
              ) : style.shape === 'x' ? (
                <span className="inline-block text-[11px] font-black leading-none" style={{ color: style.color }}>
                  ✕
                </span>
              ) : (
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: style.color, border: '1px solid #12331c' }}
                />
              ))}
            <span className="font-bold text-[#19133c]">{ZONE_LABELS[classification.zone]}</span>
            <span className="text-gray-300">|</span>
            <span className="font-semibold text-[#255393]">
              {classification.hash === 'inside' ? 'Inside hash' : 'Outside hash'}
            </span>
            <span className="text-gray-300">|</span>
            <span className="text-gray-600">{distance.toFixed(1)} yd from cage</span>
          </div>
        ) : (
          <span className="text-xs text-gray-500">No location placed</span>
        )}
        {showSkip && !value && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="shrink-0 text-xs font-semibold text-gray-400 hover:text-gray-600"
          >
            Skip location
          </button>
        )}
      </div>
    </div>
  );
};

export default ShotLocationPicker;
